import { Button, HStack, Stack, Text } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { FullModal } from '$/components/common/Modals/Modal';
import { useCloudMessageTranslation } from '$/hooks/useCloudMessageTranslation';
import { useToast } from '$/hooks/useToast';
import { type User } from '$/services/usecases/authentication/mapper/genericCloudResponse';
import { deleteStudent } from '$/services/usecases/school/students';

interface Props {
  student: User | null;
  isOpen: boolean;
  onClose: () => void;
}

export const DeleteStudentModal: FC<Props> = ({ student, isOpen, onClose }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { cloudMessageTranslation } = useCloudMessageTranslation();

  const { mutate: removeStudent, isPending } = useMutation({
    mutationFn: (studentId: string) => deleteStudent(studentId),
    onSuccess: (data) => {
      if (!data.isSuccessful) {
        toast(cloudMessageTranslation(data.response.message), 'error', '');
        return;
      }
      toast(t('school.students.deleteStudentSuccess'), 'success', '');
      onClose();
    },
  });

  if (student == null) return null;

  return (
    <FullModal
      isOpen={isOpen}
      onClose={onClose}
      header={t('school.students.deleteStudentModal.title')}
      size='xl'
      isScrollable={false}
    >
      <Stack gap='6'>
        <Text>
          {t('school.students.deleteStudentModal.description', {
            email: student.email,
          })}
        </Text>
        <HStack justify='space-between'>
          <Button isDisabled={isPending} onClick={onClose} variant='ghost'>
            {t('general.cancel')}
          </Button>
          <Button
            isLoading={isPending}
            onClick={() => removeStudent(student.id)}
            variant='danger'
          >
            {t('general.delete')}
          </Button>
        </HStack>
      </Stack>
    </FullModal>
  );
};
